/**
 * drillHandoff — hand a report drill's filter to a deals list opened in a
 * NEW tab.
 *
 * Why not the sticky deals query (utils/stickyQuery): that is sessionStorage,
 * which is per tab and is not reliably copied into a window.open'd tab — and
 * must not be, since the tab is opened with `noopener`. Writing it would also
 * have overwritten the report tab's own remembered deals filter.
 *
 * So: the report tab writes the filter to localStorage under a one-time key
 * (`bd.drill.<id>`, with a timestamp) and opens `/?drill=<id>`. The new tab's
 * deals list reads that key once, deletes it, and applies the filter as its
 * initial filter. A key older than MAX_AGE_MS is ignored (and deleted), so a
 * stale link or a reopened tab never resurrects an old drill.
 *
 * Never throws: storage can be unavailable (some private modes) or full, and
 * a stored value can be anything. Every path falls back to "no drill" — the
 * same contract as stickyQuery and uiPrefs.
 */

/** URL parameter carrying the handoff id. */
export const DRILL_PARAM = "drill";

const PREFIX = "bd.drill.";

/** How long a handoff waits to be picked up. */
export const MAX_AGE_MS = 10 * 60 * 1000;

const isFilterTree = (q) => Boolean(q && Array.isArray(q.rules));

const randomId = () => {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

/** Drop handoffs nobody picked up in time (tidying only). */
const pruneStale = (storage, now) => {
  try {
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith(PREFIX)) continue;
      let at = 0;
      try {
        at = Number(JSON.parse(storage.getItem(key))?.at) || 0;
      } catch {
        at = 0;
      }
      if (now - at > MAX_AGE_MS) storage.removeItem(key);
    }
  } catch {
    // Tidying is best-effort.
  }
};

/**
 * Store a drill filter for a new tab to pick up.
 *
 * @returns {string|null} the handoff id, or null when storage is unavailable
 *   (the caller then opens the deals list unfiltered and says so)
 */
export const stashDrill = (filter, { now = Date.now(), storage, id } = {}) => {
  try {
    const store = storage || window.localStorage;
    if (!store || !isFilterTree(filter)) return null;
    pruneStale(store, now);
    const key = id || randomId();
    store.setItem(PREFIX + key, JSON.stringify({ at: now, filter }));
    return key;
  } catch {
    return null;
  }
};

// Ids already consumed in this page, with what they held. React may run a
// state initializer twice (StrictMode); the key is deleted on the first read,
// so the second read must come from here.
const consumed = new Map();

/**
 * Read and delete a handoff. Null when missing, stale, malformed or storage
 * is unavailable.
 */
export const takeDrill = (id, { now = Date.now(), storage } = {}) => {
  if (!id) return null;
  if (consumed.has(id)) return consumed.get(id);
  let filter = null;
  try {
    const store = storage || window.localStorage;
    const raw = store?.getItem(PREFIX + id);
    store?.removeItem(PREFIX + id);
    if (raw) {
      const parsed = JSON.parse(raw);
      const fresh = now - (Number(parsed?.at) || 0) <= MAX_AGE_MS;
      if (fresh && isFilterTree(parsed?.filter)) filter = parsed.filter;
    }
  } catch {
    filter = null;
  }
  consumed.set(id, filter);
  return filter;
};

/** The handoff id in a URL search string, or null. */
export const drillIdFrom = (search) => {
  try {
    return new URLSearchParams(search || "").get(DRILL_PARAM) || null;
  } catch {
    return null;
  }
};

/** A URL search string with the handoff parameter removed ("" if empty). */
export const withoutDrillParam = (search) => {
  try {
    const params = new URLSearchParams(search || "");
    params.delete(DRILL_PARAM);
    const s = params.toString();
    return s ? `?${s}` : "";
  } catch {
    return "";
  }
};

/**
 * Delete every handoff in storage, except `keep` (the id this tab was opened
 * to pick up, if any). A handoff holds a deals filter — possibly values
 * from a report — so it is not left lying in localStorage: this runs on app
 * load (main.jsx, keeping the current URL's own id so a drill tab can still
 * read it) and on sign-out (navBarPage, keeping nothing).
 *
 * Trade-off: a drill tab still loading in another window when this tab
 * loads loses its handoff and opens without the drill filter, as it would
 * for an expired one. That window is the second or two between opening a
 * tab and its load.
 *
 * Never throws.
 */
export const clearDrills = ({ keep = null, storage } = {}) => {
  try {
    const store = storage || window.localStorage;
    if (!store) return;
    for (let i = store.length - 1; i >= 0; i -= 1) {
      const key = store.key(i);
      if (key && key.startsWith(PREFIX) && key !== PREFIX + keep) {
        store.removeItem(key);
      }
    }
  } catch {
    // Best-effort: storage unavailable means nothing was stored either.
  }
};

/** Test seam: forget consumed ids. */
export const resetConsumedDrills = () => consumed.clear();
